<template>
  <div class="system-settings">
    <!-- 页面标题 -->
    <div class="page-header">
      <div class="header-left">
        <h2 class="page-title">系统设置</h2>
        <p class="page-subtitle">管理系统配置和参数设置</p>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="isLoading" class="loading-container">
      <el-skeleton :rows="10" animated />
    </div>

    <!-- 设置选项卡 -->
    <div v-else class="settings-tabs">
      <el-tabs v-model="activeTab" type="border-card">
        <!-- 基本设置 -->
        <el-tab-pane label="基本设置" name="basic">
          <el-card shadow="never">
            <template #header>
              <div class="card-header">
                <h3>基本配置</h3>
                <p>系统基础信息配置</p>
              </div>
            </template>

            <el-form :model="basicSettings" label-width="120px">
              <el-form-item label="系统名称">
                <el-input 
                  v-model="basicSettings.systemName" 
                  placeholder="请输入系统名称"
                  style="width: 300px"
                />
              </el-form-item>

              <el-form-item label="系统Logo">
                <el-upload
                  action="#"
                  :auto-upload="false"
                  :show-file-list="false"
                  :on-change="handleLogoChange"
                >
                  <el-button type="primary">选择图片</el-button>
                  <template #tip>
                    <div class="el-upload__tip">
                      建议上传 200x60 像素的PNG图片
                    </div>
                  </template>
                </el-upload>
                <div class="logo-preview" v-if="basicSettings.logo">
                  <img :src="basicSettings.logo" alt="Logo预览" class="preview-image" />
                </div>
              </el-form-item>

              <el-form-item label="系统版本">
                <el-input 
                  v-model="basicSettings.version" 
                  placeholder="请输入系统版本"
                  style="width: 200px"
                />
              </el-form-item>

              <el-form-item label="客服公众号">
                <el-input 
                  v-model="basicSettings.customerServicePublicAccount" 
                  placeholder="请输入客服公众号"
                  maxlength="50"
                  show-word-limit
                  style="width: 300px"
                />
              </el-form-item>

              <el-form-item label="应用介绍">
                <el-input 
                  v-model="basicSettings.description" 
                  type="textarea" 
                  :rows="3"
                  placeholder="请输入系统描述"
                  style="width: 400px"
                />
              </el-form-item>

              <el-form-item>
                <el-button type="primary" @click="saveBasicSettings">
                  保存设置
                </el-button>
                <el-button @click="resetBasicSettings">
                  重置
                </el-button>
              </el-form-item>
            </el-form>
          </el-card>
        </el-tab-pane>

        <!-- 风控管理 -->
        <el-tab-pane label="风控管理" name="riskControl">
          <el-card shadow="never">
            <template #header>
              <div class="card-header">
                <h3>风控限额配置</h3>
                <p>调整各风控规则的触发阈值，修改后立即生效，无需重启服务</p>
              </div>
            </template>

            <el-alert
              title="以下限额用于防范平台资金洗钱风险，请谨慎调整。修改后将对所有新产生的奖金发放立即生效。"
              type="warning"
              show-icon
              :closable="false"
              style="margin-bottom: 20px"
            />

            <el-form :model="rcSettings" label-width="240px" v-loading="rcLoading">

              <el-divider content-position="left">① 创建者获奖审核阈值</el-divider>
              <el-form-item label="奖金池下限（元）">
                <el-input-number
                  v-model="rcSettings.rc_creator_prize_threshold"
                  :min="0" :max="10000" :step="50"
                  style="width:160px"
                />
                <span class="form-tip">奖金池超过此金额时，若创建者自己获奖则触发人工审核</span>
              </el-form-item>

              <el-divider content-position="left">② 单用户每日转账累计上限</el-divider>
              <el-form-item label="每日累计上限（元）">
                <el-input-number
                  v-model="rcSettings.rc_daily_transfer_limit"
                  :min="0" :max="100000" :step="100"
                  style="width:160px"
                />
                <span class="form-tip">单个用户每自然日累计获奖转账超过此金额时暂停转账，进入人工审核</span>
              </el-form-item>

              <el-divider content-position="left">③ 单笔大额预警阈值</el-divider>
              <el-form-item label="单笔预警金额（元）">
                <el-input-number
                  v-model="rcSettings.rc_single_prize_alert"
                  :min="0" :max="10000" :step="50"
                  style="width:160px"
                />
                <span class="form-tip">单笔奖金超过此金额时自动进入人工审核队列，审核通过后发放</span>
              </el-form-item>

              <el-divider content-position="left">④ 有奖活动创建频率限制</el-divider>
              <el-form-item label="每日最多创建数量（个）">
                <el-input-number
                  v-model="rcSettings.rc_daily_prize_activity_max"
                  :min="1" :max="20" :step="1"
                  style="width:160px"
                />
                <span class="form-tip">同一账号每自然日最多创建多少个有奖金的活动，超出则当天不允许继续创建</span>
              </el-form-item>

              <el-divider content-position="left">⑤ 高额奖金活动最少参与人数</el-divider>
              <el-form-item label="触发校验的奖金池下限（元）">
                <el-input-number
                  v-model="rcSettings.rc_min_participants_threshold"
                  :min="0" :max="10000" :step="50"
                  style="width:160px"
                />
                <span class="form-tip">奖金池超过此金额时，才执行最少参与人数检查</span>
              </el-form-item>
              <el-form-item label="最少参与人数（人）">
                <el-input-number
                  v-model="rcSettings.rc_min_participants_count"
                  :min="2" :max="20" :step="1"
                  style="width:160px"
                />
                <span class="form-tip">高额奖金活动实际参与人数低于此值时，拒绝发放并进入人工审核</span>
              </el-form-item>

              <el-form-item style="margin-top:24px">
                <el-button type="primary" :loading="rcSaving" @click="saveRcSettings">
                  保存限额配置
                </el-button>
                <el-button @click="loadRcSettings">重置为当前值</el-button>
              </el-form-item>
            </el-form>
          </el-card>
        </el-tab-pane>

        <!-- 支付设置（已隐藏，如需恢复请删除 v-if="false"） -->
        <el-tab-pane label="支付设置" name="payment" v-if="false">
          <el-card shadow="never">
            <template #header>
              <div class="card-header">
                <h3>支付接口配置</h3>
                <p>第三方支付平台配置</p>
              </div>
            </template>

            <el-tabs type="border-card">
              <!-- 微信支付 -->
              <el-tab-pane label="微信支付">
                <el-form :model="wechatPaySettings" label-width="120px">
                  <el-form-item label="AppID">
                    <el-input 
                      v-model="wechatPaySettings.appId" 
                      placeholder="请输入微信AppID"
                      style="width: 300px"
                    />
                  </el-form-item>

                  <el-form-item label="商户号">
                    <el-input 
                      v-model="wechatPaySettings.mchId" 
                      placeholder="请输入微信商户号"
                      style="width: 300px"
                    />
                  </el-form-item>

                  <el-form-item label="API密钥">
                    <el-input 
                      v-model="wechatPaySettings.apiKey" 
                      type="password"
                      placeholder="请输入API密钥"
                      style="width: 300px"
                      show-password
                    />
                  </el-form-item>

                  <el-form-item label="证书路径">
                    <el-input 
                      v-model="wechatPaySettings.certPath" 
                      placeholder="请输入证书路径"
                      style="width: 300px"
                    />
                  </el-form-item>

                  <el-form-item label="启用状态">
                    <el-switch v-model="wechatPaySettings.enabled" />
                  </el-form-item>

                  <el-form-item>
                    <el-button type="primary" @click="saveWechatPaySettings">
                      保存设置
                    </el-button>
                  </el-form-item>
                </el-form>
              </el-tab-pane>

              <!-- 支付宝 -->
              <el-tab-pane label="支付宝">
                <el-form :model="alipaySettings" label-width="120px">
                  <el-form-item label="AppID">
                    <el-input 
                      v-model="alipaySettings.appId" 
                      placeholder="请输入支付宝AppID"
                      style="width: 300px"
                    />
                  </el-form-item>

                  <el-form-item label="商户私钥">
                    <el-input 
                      v-model="alipaySettings.privateKey" 
                      type="textarea"
                      :rows="4"
                      placeholder="请输入商户私钥"
                      style="width: 400px"
                    />
                  </el-form-item>

                  <el-form-item label="支付宝公钥">
                    <el-input 
                      v-model="alipaySettings.publicKey" 
                      type="textarea"
                      :rows="4"
                      placeholder="请输入支付宝公钥"
                      style="width: 400px"
                    />
                  </el-form-item>

                  <el-form-item label="启用状态">
                    <el-switch v-model="alipaySettings.enabled" />
                  </el-form-item>

                  <el-form-item>
                    <el-button type="primary" @click="saveAlipaySettings">
                      保存设置
                    </el-button>
                  </el-form-item>
                </el-form>
              </el-tab-pane>
            </el-tabs>
          </el-card>
        </el-tab-pane>

        <!-- 安全设置 -->
        <el-tab-pane label="安全设置" name="security">
          <el-card shadow="never">
            <template #header>
              <div class="card-header">
                <h3>安全配置</h3>
                <p>系统安全相关配置</p>
              </div>
            </template>

            <el-form :model="securitySettings" label-width="120px">
              <el-form-item label="登录尝试次数">
                <el-input-number 
                  v-model="securitySettings.maxLoginAttempts" 
                  :min="1" 
                  :max="10"
                  placeholder="请输入最大登录尝试次数"
                  style="width: 120px"
                />
                <span class="form-tip">次（超过次数将锁定账户）</span>
              </el-form-item>

              <el-form-item label="登录锁定时间">
                <el-input-number 
                  v-model="securitySettings.lockoutMinutes" 
                  :min="1" 
                  :max="1440"
                  placeholder="请输入锁定时间"
                  style="width: 120px"
                />
                <span class="form-tip">分钟</span>
              </el-form-item>

              <el-form-item label="会话超时时间">
                <el-input-number 
                  v-model="securitySettings.sessionTimeout" 
                  :min="5" 
                  :max="480"
                  placeholder="请输入会话超时时间"
                  style="width: 120px"
                />
                <span class="form-tip">分钟</span>
              </el-form-item>

              <el-form-item label="密码最小长度">
                <el-input-number 
                  v-model="securitySettings.minPasswordLength" 
                  :min="6" 
                  :max="20"
                  placeholder="请输入密码最小长度"
                  style="width: 120px"
                />
                <span class="form-tip">位</span>
              </el-form-item>

              <el-form-item label="密码复杂度">
                <el-checkbox-group v-model="securitySettings.passwordComplexity">
                  <el-checkbox label="uppercase">包含大写字母</el-checkbox>
                  <el-checkbox label="lowercase">包含小写字母</el-checkbox>
                  <el-checkbox label="number">包含数字</el-checkbox>
                  <el-checkbox label="special">包含特殊字符</el-checkbox>
                </el-checkbox-group>
              </el-form-item>

              <el-form-item label="启用双因素认证">
                <el-switch v-model="securitySettings.twoFactorAuth" />
              </el-form-item>

              <el-form-item label="启用登录验证码">
                <el-switch v-model="securitySettings.loginCaptcha" />
              </el-form-item>

              <el-form-item>
                <el-button type="primary" @click="saveSecuritySettings">
                      保存设置
                </el-button>
                <el-button @click="resetSecuritySettings">
                  重置
                </el-button>
              </el-form-item>
            </el-form>
          </el-card>
        </el-tab-pane>

        <!-- 账号设置 -->
        <el-tab-pane label="账号设置" name="account">
          <!-- 管理员列表 -->
          <el-card shadow="never" class="account-settings-card">
            <template #header>
              <div class="card-header" style="display:flex;justify-content:space-between;align-items:center;">
                <div>
                  <h3 style="margin:0;">管理员账号管理</h3>
                  <p style="margin:4px 0 0;color:#909399;font-size:13px;">管理所有后台管理员账号</p>
                </div>
                <el-button type="primary" :icon="Plus" @click="openAddDialog">新增管理员</el-button>
              </div>
            </template>

            <el-table :data="adminAccounts" v-loading="accountLoading" style="width:100%">
              <el-table-column prop="username" label="用户名" width="130" />
              <el-table-column prop="nickname" label="昵称" width="130" />
              <el-table-column prop="email" label="邮箱" min-width="160" />
              <el-table-column prop="role" label="角色" width="120">
                <template #default="{ row }">
                  <el-tag :type="row.role === 'superadmin' ? 'danger' : 'primary'" size="small">
                    {{ row.role === 'superadmin' ? '超级管理员' : '管理员' }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="status" label="状态" width="90">
                <template #default="{ row }">
                  <el-tag :type="row.status === 'active' ? 'success' : 'info'" size="small">
                    {{ row.status === 'active' ? '启用' : '禁用' }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="lastLoginTime" label="最后登录" width="170" />
              <el-table-column label="操作" width="220" fixed="right">
                <template #default="{ row }">
                  <el-button size="small" @click="openEditDialog(row)">编辑</el-button>
                  <el-button size="small" type="warning" @click="openResetPasswordDialog(row)">重置密码</el-button>
                  <el-button
                    size="small"
                    :type="row.status === 'active' ? 'info' : 'success'"
                    @click="toggleAdminStatus(row)"
                  >{{ row.status === 'active' ? '禁用' : '启用' }}</el-button>
                  <el-button size="small" type="danger" @click="deleteAdmin(row)">删除</el-button>
                </template>
              </el-table-column>
            </el-table>
          </el-card>

          <!-- 新增/编辑管理员对话框 -->
          <el-dialog v-model="accountDialogVisible" :title="accountDialogMode === 'add' ? '新增管理员' : '编辑管理员'" width="480px">
            <el-form ref="accountFormRef" :model="accountForm" :rules="accountFormRules" label-width="90px">
              <el-form-item label="用户名" prop="username">
                <el-input v-model="accountForm.username" :disabled="accountDialogMode === 'edit'" placeholder="登录用户名（字母/数字/下划线）" />
              </el-form-item>
              <el-form-item label="昵称" prop="nickname">
                <el-input v-model="accountForm.nickname" placeholder="显示名称" />
              </el-form-item>
              <el-form-item v-if="accountDialogMode === 'add'" label="密码" prop="password">
                <el-input v-model="accountForm.password" type="password" show-password placeholder="至少8位，含大小写字母和数字" />
              </el-form-item>
              <el-form-item label="邮箱" prop="email">
                <el-input v-model="accountForm.email" placeholder="选填" />
              </el-form-item>
              <el-form-item label="手机" prop="phone">
                <el-input v-model="accountForm.phone" placeholder="选填" />
              </el-form-item>
              <el-form-item label="角色" prop="role">
                <el-select v-model="accountForm.role" style="width:100%">
                  <el-option label="超级管理员" value="superadmin" />
                  <el-option label="管理员" value="admin" />
                </el-select>
              </el-form-item>
            </el-form>
            <template #footer>
              <el-button @click="accountDialogVisible = false">取消</el-button>
              <el-button type="primary" :loading="accountSaving" @click="saveAdminAccount">保存</el-button>
            </template>
          </el-dialog>

          <!-- 重置密码对话框 -->
          <el-dialog v-model="resetPwdDialogVisible" title="重置管理员密码" width="420px">
            <el-form ref="resetPwdFormRef" :model="resetPwdForm" :rules="resetPwdRules" label-width="90px">
              <el-form-item label="新密码" prop="newPassword">
                <el-input v-model="resetPwdForm.newPassword" type="password" show-password placeholder="至少8位，含大小写字母和数字" />
              </el-form-item>
            </el-form>
            <template #footer>
              <el-button @click="resetPwdDialogVisible = false">取消</el-button>
              <el-button type="primary" :loading="resetPwdSaving" @click="doResetPassword">确认重置</el-button>
            </template>
          </el-dialog>
        </el-tab-pane>

        <!-- 分账比例设置 -->
        <el-tab-pane label="分账比例设置" name="shareRatios">
          <el-card shadow="never">
            <template #header>
              <div class="card-header">
                <h3>默认分账比例配置</h3>
                <p>设置全平台活动的默认分账比例参数，系统设置页面中的分账比例值表示的是组织者获得的百分比，而非平台获得的百分比。</p>                 
              </div>
            </template>

            <el-form :model="shareRatioSettings" label-width="200px">
              <el-form-item label="活动报名分账比例">
                <el-input-number 
                  v-model="shareRatioSettings.activityRevenueShareRatio" 
                  :min="0" 
                  :max="100" 
                  :step="1"
                  placeholder="请输入活动报名分账比例"
                  style="width: 120px"
                />
                <span class="form-tip">%（此比例将应用于新创建的活动）</span>
              </el-form-item>

              <el-form-item label="赞助费组织者分账比例">
                <el-input-number 
                  v-model="shareRatioSettings.sponsorshipRevenueShareRatio" 
                  :min="0" 
                  :max="100" 
                  :step="1"
                  placeholder="请输入赞助费组织者分账比例"
                  style="width: 120px"
                />
                <span class="form-tip">%（此比例将应用于新创建的活动）</span>
              </el-form-item>

              <el-form-item label="赞助费奖金池分账比例">
                <el-input-number 
                  v-model="shareRatioSettings.sponsorshipBonusPoolShareRatio" 
                  :min="0" 
                  :max="100" 
                  :step="1"
                  placeholder="请输入赞助费奖金池分账比例"
                  style="width: 120px"
                />
                <span class="form-tip">%（此比例将应用于新创建的活动）</span>
              </el-form-item>

              <el-form-item>
                <el-button type="primary" @click="saveShareRatioSettings">
                  保存设置
                </el-button>
                <el-button @click="resetShareRatioSettings">
                  重置
                </el-button>
              </el-form-item>
            </el-form>
          </el-card>
        </el-tab-pane>

        <!-- 备份与恢复 -->
        <el-tab-pane label="备份恢复" name="backup">
          <el-card shadow="never">
            <template #header>
              <div class="card-header">
                <h3>数据备份与恢复</h3>
                <p>系统数据备份和恢复操作</p>
              </div>
            </template>

            <div class="backup-actions">
              <el-button type="primary" @click="handleBackup">
                <el-icon><Download /></el-icon>
                立即备份
              </el-button>
              
              <el-button type="success" @click="handleRestoreLatest">
                <el-icon><Upload /></el-icon>
                恢复最新备份
              </el-button>

              <el-button type="warning" @click="handleCleanLogs">
                <el-icon><Delete /></el-icon>
                清理日志
              </el-button>

              <el-button type="info" @click="handleExportExcel">
                <el-icon><Download /></el-icon>
                导出Excel数据
              </el-button>
            </div>

            <!-- 备份历史 -->
            <div class="backup-history" v-if="!isLoading">
              <h4>备份历史记录</h4>
              <el-table 
                :data="backupHistory" 
                :key="backupHistory.length"
                style="width: 100%" 
                empty-text="暂无备份记录"
              >
                <el-table-column prop="name" label="备份名称" />
                <el-table-column prop="size" label="文件大小" width="100" />
                <el-table-column prop="createTime" label="备份时间" width="180" />
                <el-table-column label="操作" width="220" align="center">
                  <template #default="{ row }">
                    <div class="backup-actions">
                      <el-button size="small" type="primary" @click="handleDownloadBackup(row)">
                        <el-icon><Download /></el-icon>
                        下载
                      </el-button>
                      <el-button size="small" type="warning" @click="handleRestore(row)">
                        <el-icon><Upload /></el-icon>
                        恢复
                      </el-button>
                      <el-button 
                        size="small" 
                        type="danger" 
                        @click="handleDeleteBackup(row)"
                      >
                        <el-icon><Delete /></el-icon>
                        删除
                      </el-button>
                    </div>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </el-card>
        </el-tab-pane>

        <!-- 意见反馈管理 -->
        <el-tab-pane label="意见反馈" name="feedbacks">
          <FeedbackManagement />
        </el-tab-pane>

        <!-- 文档管理 -->
        <el-tab-pane label="文档管理" name="documents">
          <DocumentManagement />
        </el-tab-pane>
      </el-tabs>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox, ElSkeleton } from 'element-plus'
import { Plus } from '@element-plus/icons-vue'
import api from '@/utils/api'
import FeedbackManagement from './FeedbackManagement.vue'
import DocumentManagement from './DocumentManagement.vue'

export default {
  name: 'SystemSettings',
  
  components: {
    ElSkeleton,
    FeedbackManagement,
    DocumentManagement
  },
  
  setup() {
    const activeTab = ref('basic')
    
    // 基本设置
    const basicSettings = reactive({
      systemName: '赛吧运动平台',
      logo: '',
      version: '1.0.0',
      customerServicePublicAccount: '',
      description: '专业的运动社交平台，提供丰富的运动活动和社区服务'
    })
    
    // 邮件设置
    const emailSettings = reactive({
      smtpHost: 'smtp.example.com',
      smtpPort: 587,
      fromEmail: 'noreply@saiba.com',
      fromName: '赛吧运动平台',
      username: 'admin',
      password: '',
      ssl: false
    })
    
    // 微信支付设置
    const wechatPaySettings = reactive({
      appId: '',
      mchId: '',
      apiKey: '',
      certPath: '',
      enabled: true
    })
    
    // 支付宝设置
    const alipaySettings = reactive({
      appId: '',
      privateKey: '',
      publicKey: '',
      enabled: true
    })
    
    // 安全设置
    const securitySettings = reactive({
      maxLoginAttempts: 5,
      lockoutMinutes: 30,
      sessionTimeout: 120,
      minPasswordLength: 8,
      passwordComplexity: ['uppercase', 'lowercase', 'number'],
      twoFactorAuth: false,
      loginCaptcha: true
    })
    
    // 备份历史
    const backupHistory = ref([])

    // 风控限额配置
    const rcSettings = reactive({
      rc_creator_prize_threshold:   200,
      rc_daily_transfer_limit:     2000,
      rc_single_prize_alert:        500,
      rc_daily_prize_activity_max:    3,
      rc_min_participants_threshold: 200,
      rc_min_participants_count:       3
    })
    const rcLoading = ref(false)
    const rcSaving  = ref(false)
    
    // 分账比例设置
    const shareRatioSettings = reactive({
      activityRevenueShareRatio: 20, // 活动报名分账比例(%)
      sponsorshipRevenueShareRatio: 30, // 赞助费组织者分账比例(%)
      sponsorshipBonusPoolShareRatio: 30 // 赞助费奖金池分账比例(%)
    })
    
    // 密码表单相关（账号设置 Tab 已替换为管理员账号管理，保留引用避免报错）
    const passwordFormRef = ref(null)
    const passwordLoading = ref(false)
    const passwordForm = reactive({ oldPassword: '', newPassword: '', confirmPassword: '' })
    
    // 密码验证规则
    const validatePassword = (rule, value, callback) => {
      if (value.length < 8 || value.length > 20) {
        callback(new Error('密码长度必须为 8-20 个字符'))
      } else if (!/[A-Z]/.test(value)) {
        callback(new Error('密码必须包含至少一个大写字母'))
      } else if (!/[a-z]/.test(value)) {
        callback(new Error('密码必须包含至少一个小写字母'))
      } else if (!/\d/.test(value)) {
        callback(new Error('密码必须包含至少一个数字'))
      } else {
        callback()
      }
    }
    
    const validateConfirmPassword = (rule, value, callback) => {
      if (value !== passwordForm.newPassword) {
        callback(new Error('两次输入的密码不一致'))
      } else {
        callback()
      }
    }
    
    const passwordRules = {
      oldPassword: [
        { required: true, message: '请输入当前密码', trigger: 'blur' }
      ],
      newPassword: [
        { required: true, message: '请输入新密码', trigger: 'blur' },
        { validator: validatePassword, trigger: 'blur' }
      ],
      confirmPassword: [
        { required: true, message: '请确认新密码', trigger: 'blur' },
        { validator: validateConfirmPassword, trigger: 'blur' }
      ]
    }
    
    // 密码强度计算
    const passwordStrength = computed(() => {
      const password = passwordForm.newPassword
      if (!password) return 0
      
      let strength = 0
      
      // 长度
      if (password.length >= 8) strength += 20
      if (password.length >= 12) strength += 10
      if (password.length >= 16) strength += 10
      
      // 包含大写
      if (/[A-Z]/.test(password)) strength += 15
      
      // 包含小写
      if (/[a-z]/.test(password)) strength += 15
      
      // 包含数字
      if (/\d/.test(password)) strength += 15
      
      // 包含特殊字符
      if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength += 15
      
      return strength
    })
    
    const passwordStrengthColor = computed(() => {
      const strength = passwordStrength.value
      if (strength < 40) return '#F56C6C'
      if (strength < 70) return '#E6A23C'
      return '#67C23A'
    })
    
    const passwordStrengthStatus = computed(() => {
      const strength = passwordStrength.value
      if (strength < 40) return 'exception'
      if (strength < 70) return 'warning'
      return 'success'
    })
    
    const passwordStrengthText = computed(() => {
      const strength = passwordStrength.value
      if (strength < 40) return '弱'
      if (strength < 70) return '中'
      return '强'
    })
    
    // 密码修改历史
    const passwordHistory = ref([
      {
        time: '2024-01-15 10:30:00',
        ip: '192.168.1.100',
        device: 'Chrome 浏览器 / Windows 10',
        success: true
      },
      {
        time: '2024-01-10 14:20:00',
        ip: '192.168.1.100',
        device: 'Chrome 浏览器 / Windows 10',
        success: true
      }
    ])
    
    // 修改密码
    const handleChangePassword = async () => {
      if (!passwordFormRef.value) return
      
      // 防重复提交
      if (passwordLoading.value) {
        ElMessage.warning('正在处理中，请勿重复提交')
        return
      }
      
      await passwordFormRef.value.validate(async (valid) => {
        if (!valid) return
        
        passwordLoading.value = true
        
        try {
          // TODO: 调用API修改密码
          // await api.changePassword(passwordForm)
          
          // 模拟延迟
          await new Promise(resolve => setTimeout(resolve, 1000))
          
          // 添加到历史记录
          passwordHistory.value.unshift({
            time: new Date().toLocaleString(),
            ip: '192.168.1.100',
            device: 'Chrome 浏览器 / Windows 10',
            success: true
          })
          
          // 只保留最近5条
          if (passwordHistory.value.length > 5) {
            passwordHistory.value = passwordHistory.value.slice(0, 5)
          }
          
          ElMessage.success('密码修改成功，请重新登录')
          
          // 重置表单
          resetPasswordForm()
          
          // 延迟2秒后退出登录
          setTimeout(() => {
            localStorage.removeItem('admin_token')
            localStorage.removeItem('admin_user')
            window.location.href = '/login'
          }, 2000)
        } catch (error) {
          console.error('修改密码失败:', error)
          ElMessage.error('修改密码失败，请重试')
        } finally {
          passwordLoading.value = false
        }
      })
    }
    
    // 重置密码表单
    const resetPasswordForm = () => {
      passwordForm.oldPassword = ''
      passwordForm.newPassword = ''
      passwordForm.confirmPassword = ''
      if (passwordFormRef.value) {
        passwordFormRef.value.clearValidate()
      }
    }

    // ===== 管理员账号管理 =====
    const adminAccounts = ref([])
    const accountLoading = ref(false)

    const loadAdminAccounts = async () => {
      accountLoading.value = true
      try {
        const res = await api.getAdminAccounts()
        adminAccounts.value = res.data || []
      } catch (e) {
        ElMessage.error('加载管理员列表失败')
      } finally {
        accountLoading.value = false
      }
    }

    const accountDialogVisible = ref(false)
    const accountDialogMode = ref('add')
    const accountSaving = ref(false)
    const accountFormRef = ref(null)
    const accountForm = reactive({ id: null, username: '', nickname: '', password: '', email: '', phone: '', role: 'admin' })
    const accountFormRules = {
      username: [
        { required: true, message: '请输入用户名', trigger: 'blur' },
        { pattern: /^[a-zA-Z0-9_]{3,20}$/, message: '3-20位字母/数字/下划线', trigger: 'blur' }
      ],
      password: [
        { required: true, message: '请输入密码', trigger: 'blur' },
        { min: 8, message: '至少8位', trigger: 'blur' }
      ],
      email: [{ type: 'email', message: '邮箱格式不正确', trigger: 'blur' }]
    }

    const openAddDialog = () => {
      accountDialogMode.value = 'add'
      Object.assign(accountForm, { id: null, username: '', nickname: '', password: '', email: '', phone: '', role: 'admin' })
      accountDialogVisible.value = true
      accountFormRef.value?.clearValidate()
    }

    const openEditDialog = (row) => {
      accountDialogMode.value = 'edit'
      Object.assign(accountForm, { id: row.id, username: row.username, nickname: row.nickname || '', password: '', email: row.email || '', phone: row.phone || '', role: row.role || 'admin' })
      accountDialogVisible.value = true
      accountFormRef.value?.clearValidate()
    }

    const saveAdminAccount = async () => {
      if (!accountFormRef.value) return
      await accountFormRef.value.validate(async (valid) => {
        if (!valid) return
        accountSaving.value = true
        try {
          if (accountDialogMode.value === 'add') {
            await api.createAdminAccount({ username: accountForm.username, password: accountForm.password, nickname: accountForm.nickname, email: accountForm.email, phone: accountForm.phone, role: accountForm.role })
            ElMessage.success('管理员创建成功')
          } else {
            const cur = adminAccounts.value.find(a => a.id === accountForm.id)
            await api.updateAdminAccount(accountForm.id, { nickname: accountForm.nickname, email: accountForm.email, phone: accountForm.phone, role: accountForm.role, status: cur?.status || 'active' })
            ElMessage.success('管理员信息更新成功')
          }
          accountDialogVisible.value = false
          loadAdminAccounts()
        } catch (e) {
          ElMessage.error(e?.response?.data?.message || '操作失败')
        } finally {
          accountSaving.value = false
        }
      })
    }

    const toggleAdminStatus = async (row) => {
      const newStatus = row.status === 'active' ? 'disabled' : 'active'
      const label = newStatus === 'disabled' ? '禁用' : '启用'
      try {
        await ElMessageBox.confirm(`确定要${label}管理员「${row.username}」吗？`, '确认操作', { type: 'warning' })
        await api.updateAdminAccount(row.id, { nickname: row.nickname, email: row.email, phone: row.phone, role: row.role, status: newStatus })
        ElMessage.success(`已${label}`)
        loadAdminAccounts()
      } catch (e) {
        if (e !== 'cancel') ElMessage.error(e?.response?.data?.message || '操作失败')
      }
    }

    const deleteAdmin = async (row) => {
      try {
        await ElMessageBox.confirm(`确定要删除管理员「${row.username}」吗？此操作不可撤销！`, '确认删除', { type: 'error', confirmButtonText: '删除', confirmButtonClass: 'el-button--danger' })
        await api.deleteAdminAccount(row.id)
        ElMessage.success('已删除')
        loadAdminAccounts()
      } catch (e) {
        if (e !== 'cancel') ElMessage.error(e?.response?.data?.message || '删除失败')
      }
    }

    const resetPwdDialogVisible = ref(false)
    const resetPwdSaving = ref(false)
    const resetPwdFormRef = ref(null)
    const resetPwdForm = reactive({ adminId: null, newPassword: '' })
    const resetPwdRules = {
      newPassword: [{ required: true, message: '请输入新密码', trigger: 'blur' }, { min: 8, message: '至少8位', trigger: 'blur' }]
    }

    const openResetPasswordDialog = (row) => {
      resetPwdForm.adminId = row.id
      resetPwdForm.newPassword = ''
      resetPwdDialogVisible.value = true
      resetPwdFormRef.value?.clearValidate()
    }

    const doResetPassword = async () => {
      if (!resetPwdFormRef.value) return
      await resetPwdFormRef.value.validate(async (valid) => {
        if (!valid) return
        resetPwdSaving.value = true
        try {
          await api.resetAdminPassword(resetPwdForm.adminId, resetPwdForm.newPassword)
          ElMessage.success('密码重置成功')
          resetPwdDialogVisible.value = false
        } catch (e) {
          ElMessage.error(e?.response?.data?.message || '重置失败')
        } finally {
          resetPwdSaving.value = false
        }
      })
    }

    // 添加加载状态
    const isLoading = ref(true)
    
    // 处理Logo上传
    const handleLogoChange = (file) => {
      // 模拟文件处理
      basicSettings.logo = URL.createObjectURL(file.raw)
    }
    
    // 保存基本设置
    const saveBasicSettings = async () => {
      try {
        await api.updateSystemSettings({
          basic: basicSettings
        })
        ElMessage.success('基本设置保存成功')
      } catch (error) {
        console.error('保存基本设置失败:', error)
        ElMessage.error('保存基本设置失败')
      }
    }
    
    // 重置基本设置
    const resetBasicSettings = () => {
      ElMessageBox.confirm('确定要重置基本设置吗？', '确认重置', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        Object.assign(basicSettings, {
          systemName: '赛吧运动平台',
          logo: '',
          version: '1.0.0',
          customerServicePublicAccount: '',
          description: '专业的运动社交平台，提供丰富的运动活动和社区服务'
        })
        ElMessage.success('基本设置已重置')
      }).catch(() => {
        // 用户取消操作
      })
    }
    
    // 保存邮件设置
    const saveEmailSettings = async () => {
      try {
        // 模拟API调用
        await new Promise(resolve => setTimeout(resolve, 1000))
        ElMessage.success('邮件设置保存成功')
      } catch (error) {
        console.error('保存邮件设置失败:', error)
        ElMessage.error('保存邮件设置失败')
      }
    }
    
    // 重置邮件设置
    const resetEmailSettings = () => {
      ElMessageBox.confirm('确定要重置邮件设置吗？', '确认重置', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        Object.assign(emailSettings, {
          smtpHost: 'smtp.example.com',
          smtpPort: 587,
          fromEmail: 'noreply@saiba.com',
          fromName: '赛吧运动平台',
          username: 'admin',
          password: '',
          ssl: false
        })
        ElMessage.success('邮件设置已重置')
      }).catch(() => {
        // 用户取消操作
      })
    }
    
    // 保存微信支付设置
    const saveWechatPaySettings = async () => {
      try {
        await api.updateSystemSettings({
          payment: {
            wechat: wechatPaySettings
          }
        })
        ElMessage.success('微信支付设置保存成功')
      } catch (error) {
        console.error('保存微信支付设置失败:', error)
        ElMessage.error('保存微信支付设置失败')
      }
    }
    
    // 保存支付宝设置
    const saveAlipaySettings = async () => {
      try {
        await api.updateSystemSettings({
          payment: {
            alipay: alipaySettings
          }
        })
        ElMessage.success('支付宝设置保存成功')
      } catch (error) {
        console.error('保存支付宝设置失败:', error)
        ElMessage.error('保存支付宝设置失败')
      }
    }
    
    // 保存安全设置
    const saveSecuritySettings = async () => {
      try {
        await api.updateSystemSettings({
          security: securitySettings
        })
        ElMessage.success('安全设置保存成功')
      } catch (error) {
        console.error('保存安全设置失败:', error)
        ElMessage.error('保存安全设置失败')
      }
    }
    
    // 重置安全设置
    const resetSecuritySettings = () => {
      ElMessageBox.confirm('确定要重置安全设置吗？', '确认重置', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        Object.assign(securitySettings, {
          maxLoginAttempts: 5,
          lockoutMinutes: 30,
          sessionTimeout: 120,
          minPasswordLength: 8,
          passwordComplexity: ['uppercase', 'lowercase', 'number'],
          twoFactorAuth: false,
          loginCaptcha: true
        })
        ElMessage.success('安全设置已重置')
      }).catch(() => {
        // 用户取消操作
      })
    }
    
    // 测试邮件设置
    const testEmailSettings = async () => {
      try {
        await api.testEmail()
        ElMessage.success('邮件发送测试成功')
      } catch (error) {
        console.error('邮件发送测试失败:', error)
        ElMessage.error('邮件发送测试失败')
      }
    }
    
    // 处理备份
    const handleBackup = async () => {
      try {
        // 调用真实API执行备份
        const response = await api.backupDatabase()
        
        // 添加到备份历史
        backupHistory.value.unshift({
          name: response.data.fileName,
          size: response.data.fileSize,
          createTime: new Date().toLocaleString()
        })
        
        ElMessage.success('数据备份成功')
      } catch (error) {
        console.error('数据备份失败:', error)
        ElMessage.error('数据备份失败')
      }
    }

    // 处理数据恢复（从备份历史中选择文件恢复）
    const handleRestore = async (row) => {
      const filename = row ? row.name : null
      if (!filename) {
        ElMessage.warning('请选择要恢复的备份文件')
        return
      }
      ElMessageBox.confirm(
        `确定要恢复备份"${filename}"吗？此操作将覆盖当前数据，且无法撤销！`,
        '确认恢复',
        {
          confirmButtonText: '确定恢复',
          cancelButtonText: '取消',
          type: 'warning'
        }
      ).then(async () => {
        try {
          await api.restoreDatabase(filename)
          ElMessage.success(`备份"${filename}"恢复成功`)
        } catch (error) {
          console.error('数据恢复失败:', error)
          ElMessage.error(error?.response?.data?.message || '数据恢复失败')
        }
      }).catch(() => {
        // 用户取消操作
      })
    }

    // 恢复最新备份
    const handleRestoreLatest = async () => {
      if (!backupHistory.value || backupHistory.value.length === 0) {
        ElMessage.warning('暂无可用的备份文件')
        return
      }
      const latest = backupHistory.value[0]
      await handleRestore(latest)
    }

    // 导出Excel数据
    const handleExportExcel = async () => {
      try {
        // 显示可选表列表
        const tablesRes = await api.getExportTables()
        if (!tablesRes.success) {
          ElMessage.error(tablesRes.message || '获取导出表列表失败')
          return
        }
        
        const allTables = tablesRes.data
        let selectedTables = []
        
        // 让用户选择要导出的表
        try {
          const { value } = await ElMessageBox.prompt(
            `请输入要导出的表编号（用逗号分隔，或输入"all"导出全部）：\n\n${allTables.map((t, i) => `${i + 1}. ${t.name}`).join('\n')}`,
            '选择导出数据',
            {
              confirmButtonText: '开始导出',
              cancelButtonText: '取消',
              inputPattern: /^(all|\d+(,\d+)*)$/,
              inputErrorMessage: '请输入正确的格式（如：1,2,3 或 all）'
            }
          )
          
          if (value === 'all') {
            selectedTables = allTables.map(t => t.key)
          } else {
            const indices = value.split(',').map(Number).filter(n => n >= 1 && n <= allTables.length)
            selectedTables = indices.map(i => allTables[i - 1].key)
          }
          
          if (selectedTables.length === 0) {
            ElMessage.warning('请选择至少一个表')
            return
          }
        } catch {
          // 用户取消
          return
        }
        
        // 显示加载状态
        const loading = ElLoading.service({
          text: '正在生成Excel文件...',
          background: 'rgba(0, 0, 0, 0.7)'
        })
        
        // 调用后端导出API
        const exportRes = await api.exportExcel(selectedTables)
        
        loading.close()
        
        if (exportRes.success) {
          const { filename, downloadUrl, tables, totalTables } = exportRes.data
          
          ElMessageBox.alert(
            `已成功导出 ${totalTables} 个表：\n${tables.join('、')}`,
            '导出成功',
            {
              confirmButtonText: '立即下载',
              showCancelButton: true,
              cancelButtonText: '稍后下载'
            }
          ).then(async () => {
            // 点击确定，下载文件
            try {
              const blobData = await api.downloadExcel(filename)
              const url = window.URL.createObjectURL(new Blob([blobData]))
              const link = document.createElement('a')
              link.href = url
              link.setAttribute('download', filename)
              document.body.appendChild(link)
              link.click()
              link.remove()
              window.URL.revokeObjectURL(url)
              ElMessage.success('下载成功')
            } catch (dlErr) {
              let errorMsg = '下载失败'
              if (dlErr.response?.data instanceof Blob) {
                try {
                  const text = await dlErr.response.data.text()
                  const errObj = JSON.parse(text)
                  if (errObj.message) errorMsg = errObj.message
                } catch (e) {}
              }
              ElMessage.error(errorMsg)
            }
          }).catch(() => {
            // 点击取消，提示用户保存下载URL
            ElMessage.info(`文件已生成，可通过API下载：${downloadUrl}`)
          })
        } else {
          ElMessage.error(exportRes.message || '导出失败')
        }
      } catch (error) {
        console.error('Excel导出失败:', error)
        ElMessage.error(error?.response?.data?.message || 'Excel导出失败')
      }
    }

    // 清理日志
    const handleCleanLogs = async () => {
      ElMessageBox.confirm('确定要清理所有日志吗？此操作不可恢复', '确认清理', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          // 调用真实API清理日志
          await api.cleanLogs()
          ElMessage.success('日志清理成功')
        } catch (error) {
          console.error('日志清理失败:', error)
          ElMessage.error('日志清理失败')
        }
      }).catch(() => {
        // 用户取消操作
      })
    }

    // 下载备份
    const handleDownloadBackup = async (backup) => {
      try {
        // 由于响应拦截器已解包 response.data，这里直接得到 blob 数据
        const blobData = await api.downloadBackup(backup.name)
        
        // 创建下载链接
        const url = window.URL.createObjectURL(new Blob([blobData]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', backup.name)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)

        ElMessage.success('备份下载成功')
      } catch (error) {
        // blob 类型错误响应需要特殊处理
        let errorMsg = '备份下载失败'
        if (error.response?.data instanceof Blob) {
          try {
            const text = await error.response.data.text()
            const errObj = JSON.parse(text)
            if (errObj.message) errorMsg = errObj.message
          } catch (e) {
            // 无法解析的话使用默认提示
          }
        } else if (error.response?.data?.message) {
          errorMsg = error.response.data.message
        }
        console.error('备份下载失败:', error)
        ElMessage.error(errorMsg)
      }
    }

    // 删除备份
    const handleDeleteBackup = async (backup) => {
      ElMessageBox.confirm(`确定要删除备份文件"${backup.name}"吗？`, '确认删除', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          // 调用真实API删除备份
          await api.deleteBackup(backup.name)
          
          // 从备份历史中移除
          backupHistory.value = backupHistory.value.filter(item => item.name !== backup.name)
          
          ElMessage.success('备份删除成功')
        } catch (error) {
          console.error('备份删除失败:', error)
          ElMessage.error('备份删除失败')
        }
      }).catch(() => {
        // 用户取消操作
      })
    }

    onMounted(async () => {
      loadAdminAccounts()
      loadRcSettings()
      try {
        // 调用真实API加载所有设置数据
        const settingsResponse = await api.getSystemSettings()
        const backupResponse = await api.getBackups()
        
        // 确保settingsResponse.data存在
        const settingsData = settingsResponse.data || {}
        
        // 更新基本设置 - 安全检查
        if (settingsData.basic) {
          Object.assign(basicSettings, settingsData.basic)
        }
        
        // 更新支付设置 - 安全检查
        if (settingsData.payment && settingsData.payment.wechat) {
          Object.assign(wechatPaySettings, settingsData.payment.wechat)
        }
        if (settingsData.payment && settingsData.payment.alipay) {
          Object.assign(alipaySettings, settingsData.payment.alipay)
        }
        
        // 更新安全设置 - 安全检查
        if (settingsData.security) {
          Object.assign(securitySettings, settingsData.security)
        }
        
        // 更新备份历史 - 安全检查
        if (backupResponse.data && backupResponse.data.history) {
          backupHistory.value = backupResponse.data.history
        }
        
        // 更新分账比例设置 - 安全检查
        if (settingsData.shareRatios) {
          Object.assign(shareRatioSettings, settingsData.shareRatios)
        }
        
      } catch (error) {
        console.error('加载设置失败:', error)
        ElMessage.error('加载设置失败')
      } finally {
        // 确保加载状态被设置为false
        isLoading.value = false
      }
    })
    
    // 加载风控限额配置
    const loadRcSettings = async () => {
      rcLoading.value = true
      try {
        const res = await api.getRiskControlConfig()
        if (res.data) Object.assign(rcSettings, res.data)
      } catch (e) {
        ElMessage.error('加载风控配置失败')
      } finally {
        rcLoading.value = false
      }
    }

    // 保存风控限额配置
    const saveRcSettings = async () => {
      rcSaving.value = true
      try {
        await api.updateRiskControlConfig({ ...rcSettings })
        ElMessage.success('风控限额配置已保存')
      } catch (e) {
        ElMessage.error('保存风控配置失败')
      } finally {
        rcSaving.value = false
      }
    }

    // 保存分账比例设置
    const saveShareRatioSettings = async () => {
      try {
        await api.updateSystemSettings({
          shareRatios: shareRatioSettings
        })
        ElMessage.success('分账比例设置保存成功')
      } catch (error) {
        console.error('保存分账比例设置失败:', error)
        ElMessage.error('保存分账比例设置失败')
      }
    }
    
    // 重置分账比例设置
    const resetShareRatioSettings = () => {
      ElMessageBox.confirm('确定要重置分账比例设置吗？', '确认重置', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        Object.assign(shareRatioSettings, {
          activityRevenueShareRatio: 20,
          sponsorshipRevenueShareRatio: 30,
          sponsorshipBonusPoolShareRatio: 30
        })
        ElMessage.success('分账比例设置已重置')
      }).catch(() => {
        // 用户取消操作
      })
    }
    
    return {
      activeTab,
      basicSettings,
      emailSettings,
      wechatPaySettings,
      alipaySettings,
      securitySettings,
      shareRatioSettings,
      backupHistory,
      isLoading,
      
      // 密码相关
      passwordFormRef,
      passwordForm,
      passwordRules,
      passwordLoading,
      passwordStrength,
      passwordStrengthColor,
      passwordStrengthStatus,
      passwordStrengthText,
      passwordHistory,
      handleChangePassword,
      resetPasswordForm,

      // 管理员账号管理
      adminAccounts,
      accountLoading,
      accountDialogVisible,
      accountDialogMode,
      accountSaving,
      accountFormRef,
      accountForm,
      accountFormRules,
      openAddDialog,
      openEditDialog,
      saveAdminAccount,
      toggleAdminStatus,
      deleteAdmin,
      resetPwdDialogVisible,
      resetPwdSaving,
      resetPwdFormRef,
      resetPwdForm,
      resetPwdRules,
      openResetPasswordDialog,
      doResetPassword,
      Plus,
      
      handleLogoChange,
      saveBasicSettings,
      resetBasicSettings,
      testEmailSettings,
      saveEmailSettings,
      resetEmailSettings,
      saveWechatPaySettings,
      saveAlipaySettings,
      saveSecuritySettings,
      resetSecuritySettings,
      saveShareRatioSettings,
      resetShareRatioSettings,
      handleBackup,
      handleRestore,
      handleRestoreLatest,
      handleExportExcel,
      handleCleanLogs,
      handleDownloadBackup,
      handleDeleteBackup,

      // 风控限额
      rcSettings,
      rcLoading,
      rcSaving,
      loadRcSettings,
      saveRcSettings
    }
  }
}
</script>

<style scoped>
.system-settings {
  padding: 20px;
}

.page-header {
  margin-bottom: 24px;
}

.loading-container {
  margin-top: 20px;
  padding: 20px;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.settings-tabs {
  margin-top: 20px;
}

.card-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.card-header h3 {
  margin: 0;
  color: #303133;
  font-size: 16px;
}

.card-header p {
  margin: 0;
  color: #909399;
  font-size: 12px;
}

.logo-preview {
  margin-top: 8px;
}

.preview-image {
  max-width: 200px;
  max-height: 60px;
  border: 1px solid #e6e8eb;
  border-radius: 4px;
  padding: 4px;
}

.form-tip {
  margin-left: 8px;
  color: #909399;
  font-size: 12px;
}

.backup-actions {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
}

.backup-history {
  margin-top: 24px;
}

.backup-history h4 {
  margin-bottom: 16px;
  color: #303133;
  font-size: 14px;
}

/* 备份操作按钮组 - 保证三个按钮在同一行 */
.backup-actions {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
  white-space: nowrap;
}

.backup-actions .el-button {
  margin-left: 0 !important;
  padding-left: 8px;
  padding-right: 8px;
}

/* 账号设置样式优化 */
.account-settings-card {
  margin-bottom: 16px;
}

/* 密码表单样式 */
.password-form {
  max-width: 600px;
}

/* 优化表单项间距 */
.password-form :deep(.el-form-item) {
  margin-bottom: 18px;
}

.password-form .form-item-compact {
  margin-bottom: 16px !important;
}

.password-form .form-item-submit {
  margin-top: 24px;
  margin-bottom: 0;
}

/* 密码输入包装器 */
.password-input-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0;
}

/* 密码强度样式优化 */
.password-strength {
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.strength-text {
  font-size: 13px;
  font-weight: 500;
  min-width: 30px;
  color: #606266;
}

/* 密码提示样式优化 */
.password-tips {
  margin-top: 8px;
  padding: 10px 12px;
  background: #f4f4f5;
  border-radius: 4px;
  border-left: 3px solid #409eff;
  line-height: 1.6;
}

.password-tips :deep(.el-text) {
  line-height: 1.8;
}

/* 密码修改历史卡片间距优化 */
.password-history-card {
  margin-top: 16px;
}

/* 卡片内边距优化 */
.account-settings-card :deep(.el-card__body) {
  padding: 20px 24px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .backup-actions {
    flex-direction: column;
    align-items: stretch;
  }
  
  .backup-actions .el-button {
    margin-bottom: 8px;
  }
}
</style>